/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */

import React, { useState } from 'react';

import { useIntl } from 'react-intl';
import { useFragment } from 'react-relay';
import { useRouterActions } from 'react-resource-router';
import { graphql } from 'relay-runtime';

import Button from '@atlaskit/button/button';
import { cssMap, jsx } from '@atlaskit/css';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import PageIcon from '@atlaskit/icon/core/page';
import ButtonItem from '@atlaskit/menu/button-item';
import Section from '@atlaskit/menu/section';
import Motion from '@atlaskit/motion/entering/motion';
import ExitingPersistence from '@atlaskit/motion/exiting-persistence';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- TODO: migrate to @atlaskit/primitives/compiled
import Text from '@atlaskit/primitives/text';
import { token } from '@atlaskit/tokens';
import {
	HUB_FEATURES,
	useIsHubFeatureEnabled,
} from '@atlassian/company-hub-common/use-is-hub-feature-enabled';

import type { portalDropdown_AtlassianHubAppShell$key } from './__generated__/portalDropdown_AtlassianHubAppShell.graphql';
import messages from './messages';
export { getPortalsFilter } from '../query-variables';

const styles = cssMap({
	overflowHidden: {
		overflow: 'hidden',
	},
	dropdownItems: {
		paddingLeft: token('space.100'),
	},
});

const motionStyles = cssMap({
	slideEntering: {
		animationName: `${token('motion.keyframe.slide.in.top.short')}, ${token('motion.keyframe.fade.in')}`,
		animationDuration: token('motion.duration.medium'),
		animationTimingFunction: token('motion.easing.out.practical'),
	},
	slideExiting: {
		animationName: `${token('motion.keyframe.slide.out.top.short')}, ${token('motion.keyframe.fade.out')}`,
		animationDuration: token('motion.duration.medium'),
		animationTimingFunction: token('motion.easing.in.practical'),
	},
});

export const PortalDropdown = ({
	queryRef,
	textColor,
	isMobile,
}: {
	isMobile?: boolean;
	queryRef: portalDropdown_AtlassianHubAppShell$key | null;
	textColor?: string;
}): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { push } = useRouterActions();
	const [isExpanded, setIsExpanded] = useState(false);
	const [isConfigurableSiteTreeEnabled] = useIsHubFeatureEnabled([
		HUB_FEATURES.CONFIGURABLE_SITE_TREE,
	]);

	const data = useFragment(
		graphql`
			fragment portalDropdown_AtlassianHubAppShell on HelpCenter
			@argumentDefinitions(portalsFilter: { type: "HelpCenterPortalFilter", defaultValue: null }) {
				portals(portalsFilter: $portalsFilter) {
					portalsList {
						id
						name
					}
				}
			}
		`,
		queryRef,
	);

	const portalPages = data?.portals?.portalsList;
	if (!portalPages || portalPages.length === 0) {
		return null;
	}

	const hasSinglePortal = portalPages.length === 1;

	if (isMobile) {
		if (hasSinglePortal) {
			return (
				<ButtonItem
					onClick={() => {
						push(`/portal/${portalPages[0]?.id}`);
					}}
				>
					{portalPages[0]?.name}
				</ButtonItem>
			);
		}

		return (
			<>
				<ButtonItem
					iconAfter={
						isExpanded ? (
							<ChevronUpIcon label={formatMessage(messages.portalUpIcon)} />
						) : (
							<ChevronDownIcon label={formatMessage(messages.portalDownIcon)} />
						)
					}
					onClick={() => setIsExpanded((prev) => !prev)}
					aria-expanded={isExpanded}
				>
					<Text weight="medium">{formatMessage(messages.portals)}</Text>
				</ButtonItem>
				<div css={styles.overflowHidden}>
					<ExitingPersistence appear>
						{isExpanded && (
							<Motion
								enteringAnimationXcss={motionStyles.slideEntering}
								exitingAnimationXcss={motionStyles.slideExiting}
							>
								<div css={styles.dropdownItems} data-testid="portal-dropdown-items">
									<Section>
										{portalPages?.map((portal) => (
											<ButtonItem
												key={portal?.id}
												onClick={() => {
													push(`/portal/${portal?.id}`);
												}}
											>
												{portal?.name}
											</ButtonItem>
										))}
									</Section>
								</div>
							</Motion>
						)}
					</ExitingPersistence>
				</div>
			</>
		);
	}

	if (hasSinglePortal) {
		return (
			<Button
				appearance="subtle"
				onClick={() => {
					push(`/portal/${portalPages[0]?.id}`);
				}}
				style={{ color: textColor }}
			>
				{portalPages[0]?.name}
			</Button>
		);
	}

	return (
		<DropdownMenu<HTMLButtonElement>
			trigger={({ triggerRef, ...props }) => (
				<Button
					{...props}
					appearance="subtle"
					style={{ color: props.isSelected ? undefined : textColor }}
					iconAfter={<ChevronDownIcon label={formatMessage(messages.portalDownIcon)} />}
					ref={triggerRef}
				>
					{formatMessage(messages.portals)}
				</Button>
			)}
			shouldRenderToParent
		>
			<DropdownItemGroup>
				{portalPages?.map((portal) => (
					<DropdownItem
						key={portal?.id}
						onClick={() => {
							push(`/portal/${portal?.id}`);
						}}
						elemBefore={
							isConfigurableSiteTreeEnabled ? undefined : (
								<PageIcon label={formatMessage(messages.pageIcon)} />
							)
						}
					>
						{portal?.name}
					</DropdownItem>
				))}
			</DropdownItemGroup>
		</DropdownMenu>
	);
};
